// FLAGS: none
// The six-slot one-time-code field the TOTP step renders: numeric only, auto-submits on the last digit.
import { KeyboardEvent, useState } from "react";
import { Button } from "@openup/shared/primitives/ui/button";
import { cn } from "@openup/shared/lib/utils";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@openup/shared/primitives/ui/input-otp";

interface CodeInputProps {
  onSubmit: (code: string) => void;
  codeLength?: number;
  busy?: boolean;
  error?: boolean;
}

const CodeInput = ({
  onSubmit,
  codeLength = 6,
  busy = false,
  error = false,
}: CodeInputProps) => {
  const [code, setCode] = useState("");
  const isComplete = code.length === codeLength;

  const submit = (value: string) => {
    if (!busy) {
      onSubmit(value);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && isComplete) {
      submit(code);
    }
  };

  return (
    <div className="flex flex-col items-center gap-6">
      <InputOTP
        maxLength={codeLength}
        value={code}
        onChange={setCode}
        onComplete={submit}
        onKeyDown={onKeyDown}
        pattern={"^\\d+$"}
        inputMode="numeric"
        autoComplete="one-time-code"
        aria-label="Verification code"
        disabled={busy}
      >
        <InputOTPGroup className="gap-3">
          {Array.from({ length: codeLength }, (_, index) => (
            <InputOTPSlot
              key={index}
              index={index}
              className={cn(
                "h-12 w-10 rounded-none border-0 border-b-2 bg-transparent text-base shadow-none first:rounded-none first:border-l-0 last:rounded-none data-[active=true]:ring-0",
                error
                  ? "border-red-600"
                  : "border-gray-400 data-[active=true]:border-indigo-800",
              )}
            />
          ))}
        </InputOTPGroup>
      </InputOTP>
      {error && (
        <p className="text-sm text-red-600">
          The code is incorrect or expired.
        </p>
      )}
      <Button
        variant="default"
        size="default"
        onClick={() => submit(code)}
        disabled={!isComplete || busy}
      >
        {busy ? "Verifying…" : "Verify"}
      </Button>
    </div>
  );
};

export default CodeInput;
